import React, { useEffect, useMemo, useState } from 'react';
import { HbrDomTable, HbrPagination } from '@harbor/elements-react-standalone';
import { useNavigate } from 'react-router-dom';

import { useCorrelatedUserEvents } from '../../../hooks/useMoodleEventBooking';
import { useFetchUser } from '../../../hooks/user-hooks';
import {
    buildCourseDetailPath,
    buildEventDetailPath,
    EventRouteBase,
} from '../../../support/moodleDetailRoutes';

const PAGE_SIZE_OPTIONS = [30, 50, 100];
const DAYS = [7, 30, 90, 180, 365] as const;

type SortKey =
  | 'event_date_desc'
  | 'event_date_asc'
  | 'title_asc'
  | 'title_desc'
  | 'registrants_desc';

type RoleFilter = '' | 'owner' | 'proctor' | 'associated';

export type TimePreset =
  | 'past_7'
  | 'past_30'
  | 'past_90'
  | 'past_180'
  | 'past_365'
  | 'future_7'
  | 'future_30'
  | 'future_90'
  | 'future_180'
  | 'future_365';

type EventApiItem = {
    event_id: number;
    event_name: string;
    course_ids: number[];
    course_names: string[];
    start_date?: string | null;
    end_date?: string | null;
    current_registrants?: number;
    owners?: string[];
    proctors?: string[];
    user_association?: {
        email?: string | null;
        is_owner?: boolean;
        is_proctor?: boolean;
        is_associated?: boolean;
    };
    location?: {
        name?: string | null;
        city?: string | null;
        state?: string | null;
        country?: string | null;
        geo?: string | null;
    };
};

type EventRow = {
    id: string;
    event_id: number;
    title: string;
    event_date?: string;
    event_end_date?: string;
    event_date_ts: number;
    event_end_date_ts: number;
    is_owner: boolean;
    is_proctor: boolean;
    is_associated: boolean;
    course_ids: number[];
    course_names: string[];
    current_registrants: number;
    location_label: string;
    roles: string;
    courses: string;
    location: string;
};

type WorkshopsUserEventsTableProps = {
    email: string;
    defaultTimePreset?: TimePreset;
    allowedDirection?: 'past' | 'future' | 'both';
    restrictToUserAssociation?: boolean;
    eventRouteBase?: EventRouteBase;
};

function safeId(n: any) {
    return `ev_${String(n).replace(/[^a-z0-9_-]/gi, '_')}`;
}

function parseApiDate(value?: string | null): Date | null {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const d = new Date(normalized);

    if (!Number.isFinite(d.getTime())) return null;
    return d;
}

function toTs(s?: string | null) {
    const d = parseApiDate(s);
    return d ? d.getTime() : 0;
}

function presetToDays(preset: TimePreset): { days_past: number; days_future: number } {
    const [dir, daysStr] = preset.split('_') as ['past' | 'future', string];
    const days = Number(daysStr);
    if (dir === 'past') return { days_past: days, days_future: 0 };
    return { days_past: 0, days_future: days };
}

function getPresetOptions(direction: 'past' | 'future' | 'both'): TimePreset[] {
    if (direction === 'past') return DAYS.map((d) => `past_${d}` as TimePreset);
    if (direction === 'future') return DAYS.map((d) => `future_${d}` as TimePreset);
    return [
        ...DAYS.map((d) => `past_${d}` as TimePreset),
        ...DAYS.map((d) => `future_${d}` as TimePreset),
    ];
}

function formatPresetLabel(preset: TimePreset) {
    const [dir, days] = preset.split('_');
    return dir === 'past' ? `Past ${days} days` : `Future ${days} days`;
}

function normalizeEmail(value: any) {
    const s = String(value ?? '').trim().toLowerCase();
    if (!s) return '';
    return s.includes('@') ? s : `${s}@cisco.com`;
}

function normalizeCourseIds(raw: any): number[] {
    if (Array.isArray(raw)) {
        return raw.map((x) => Number(x)).filter(Number.isFinite);
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((x) => Number(x)).filter(Number.isFinite);
            }
            const single = Number(parsed);
            return Number.isFinite(single) ? [single] : [];
        } catch {
            if (trimmed.includes(',')) {
                return trimmed
                  .split(',')
                  .map((x) => Number(x.trim()))
                  .filter(Number.isFinite);
            }
            const single = Number(trimmed);
            return Number.isFinite(single) ? [single] : [];
        }
    }

    const single = Number(raw);
    return Number.isFinite(single) ? [single] : [];
}

function normalizeStringArray(value: any): string[] {
    if (Array.isArray(value)) {
        return value
          .map((x) => String(x ?? '').trim())
          .filter(Boolean);
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed
                  .map((x) => String(x ?? '').trim())
                  .filter(Boolean);
            }
        } catch {
            // ignore
        }

        if (trimmed.includes(',')) {
            return trimmed.split(',').map((x) => x.trim()).filter(Boolean);
        }

        return [trimmed];
    }

    return [];
}

function normalizeEmailArray(value: any): string[] {
    if (!Array.isArray(value)) return [];
    return value.map((item) => normalizeEmail(item)).filter(Boolean);
}

function formatLocation(loc?: EventApiItem['location']) {
    if (!loc) return '—';
    const parts = [loc.name, loc.city, loc.state, loc.country]
      .map((v) => String(v ?? '').trim())
      .filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
}

function formatRoles(isOwner: boolean, isProctor: boolean, isAssociated: boolean) {
    if (isOwner && isProctor) return 'Owner + Proctor';
    if (isOwner) return 'Owner';
    if (isProctor) return 'Proctor';
    if (isAssociated) return 'Associated';
    return '—';
}

function formatCourses(courseNames: string[], courseIds: number[]) {
    if (courseNames.length) return courseNames.join(', ');
    if (courseIds.length) return courseIds.map((cid) => `Course ${cid}`).join(', ');
    return '—';
}

function mapSalesLevel1ToGeo(value?: string | null): 'amer' | 'emea' | 'apjc' | '' {
    const v = String(value ?? '').trim().toLowerCase();

    if (!v) return '';
    if (v === 'americas' || v === 'america' || v === 'amer' || v.includes('americas')) return 'amer';
    if (v === 'emea' || v.includes('emea') || v.includes('europe') || v.includes('middle east') || v.includes('africa')) return 'emea';
    if (v === 'apjc' || v.includes('apjc') || v.includes('asia pacific') || v.includes('japan') || v.includes('greater china')) return 'apjc';

    return '';
}

export default function WorkshopsUserEventsTable({
                                                     email,
                                                     defaultTimePreset = 'past_365',
                                                     allowedDirection = 'both',
                                                     restrictToUserAssociation = false,
                                                     eventRouteBase = '/workshops/event-summary',
                                                 }: WorkshopsUserEventsTableProps) {
    const navigate = useNavigate();

    const { data: dbUser } = useFetchUser(
      { email, detailed: true },
      { enabled: !!email }
    );

    const salesLevel1 = (dbUser as any)?.sales_level_1 ?? null;
    const userGeo = useMemo(() => mapSalesLevel1ToGeo(salesLevel1), [salesLevel1]);

    const presetOptions = useMemo(
      () => getPresetOptions(allowedDirection),
      [allowedDirection]
    );

    const [timePreset, setTimePreset] = useState<TimePreset>(
      presetOptions.includes(defaultTimePreset) ? defaultTimePreset : presetOptions[0]
    );

    const { days_past, days_future } = useMemo(
      () => presetToDays(timePreset),
      [timePreset]
    );

    const shouldRestrictAssociation =
      restrictToUserAssociation || timePreset.startsWith('past_');

    const effectiveUserGeo =
      timePreset.startsWith('future_') ? userGeo || undefined : undefined;

    const { data, isLoading, error } = useCorrelatedUserEvents(
      email,
      {
          days_past,
          days_future,
          size: 500,
          restrict_to_user_association: shouldRestrictAssociation,
          user_geo: effectiveUserGeo,
      } as any,
      {
          enabled: !!email,
      }
    );

    const apiItems: EventApiItem[] = useMemo(() => {
        const d: any = data;

        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.items)) return d.items;
        if (Array.isArray(d?.results)) return d.results;

        if (Array.isArray(d?.data)) return d.data;
        if (Array.isArray(d?.data?.items)) return d.data.items;
        if (Array.isArray(d?.data?.results)) return d.data.results;

        if (Array.isArray(d?.payload)) return d.payload;
        if (Array.isArray(d?.payload?.items)) return d.payload.items;
        if (Array.isArray(d?.payload?.results)) return d.payload.results;

        return [];
    }, [data]);

    const normalizedEmail = useMemo(() => normalizeEmail(email), [email]);

    const allRowsBeforeAssociationFilter: EventRow[] = useMemo(() => {
        return apiItems
          .map((ev: EventApiItem, index: number) => {
              const owners = normalizeEmailArray(ev?.owners);
              const proctors = normalizeEmailArray(ev?.proctors);

              const assoc = ev?.user_association ?? {};
              const is_owner =
                assoc.is_owner === true || owners.includes(normalizedEmail);
              const is_proctor =
                assoc.is_proctor === true || proctors.includes(normalizedEmail);
              const is_associated =
                assoc.is_associated === true || is_owner || is_proctor;

              const course_ids = normalizeCourseIds(ev?.course_ids);
              const course_names = normalizeStringArray(ev?.course_names);

              const event_date = ev?.start_date ?? undefined;
              const event_end_date = ev?.end_date ?? undefined;
              const location_label = formatLocation(ev?.location);

              return {
                  id: safeId(ev?.event_id ?? index),
                  event_id: Number(ev?.event_id ?? index),
                  title: String(ev?.event_name ?? ''),
                  event_date,
                  event_end_date,
                  event_date_ts: toTs(event_date),
                  event_end_date_ts: toTs(event_end_date),
                  is_owner,
                  is_proctor,
                  is_associated,
                  course_ids,
                  course_names,
                  current_registrants: Number(ev?.current_registrants ?? 0),
                  location_label,
                  roles: formatRoles(is_owner, is_proctor, is_associated),
                  courses: formatCourses(course_names, course_ids),
                  location: location_label,
              };
          })
          .filter((r) => !!r.title);
    }, [apiItems, normalizedEmail]);

    const rows: EventRow[] = useMemo(() => {
        const shouldRestrict =
          restrictToUserAssociation || timePreset.startsWith('past_');

        return allRowsBeforeAssociationFilter.filter((r) => {
            if (!shouldRestrict) return true;
            return r.is_associated;
        });
    }, [allRowsBeforeAssociationFilter, restrictToUserAssociation, timePreset]);

    const [roleFilter, setRoleFilter] = useState<RoleFilter>('');
    const [courseFilterName, setCourseFilterName] = useState<string>('');
    const [courseFilterId, setCourseFilterId] = useState<string>('');
    const [sortBy, setSortBy] = useState<SortKey>('event_date_desc');

    const allCourseNameOptions = useMemo(() => {
        const set = new Set<string>();
        rows.forEach((r) => r.course_names.forEach((name) => set.add(name)));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const courseIdOptions = useMemo(() => {
        const ids = Array.from(new Set(rows.flatMap((r) => r.course_ids))).sort((a, b) => a - b);
        return ids.map((id) => ({ id, label: `Course ${id}` }));
    }, [rows]);

    const filtered = useMemo(() => {
        let out = [...rows];

        if (roleFilter === 'owner') out = out.filter((r) => r.is_owner);
        if (roleFilter === 'proctor') out = out.filter((r) => r.is_proctor);
        if (roleFilter === 'associated') out = out.filter((r) => r.is_associated);

        if (courseFilterId) {
            const cid = Number(courseFilterId);
            out = out.filter((r) => r.course_ids.includes(cid));
        }

        if (courseFilterName) {
            out = out.filter((r) => r.course_names.includes(courseFilterName));
        }

        return out;
    }, [rows, roleFilter, courseFilterId, courseFilterName]);

    const sorted = useMemo(() => {
        const out = [...filtered];
        out.sort((a, b) => {
            if (sortBy === 'event_date_desc') return b.event_date_ts - a.event_date_ts;
            if (sortBy === 'event_date_asc') return a.event_date_ts - b.event_date_ts;
            if (sortBy === 'title_asc') return a.title.localeCompare(b.title);
            if (sortBy === 'title_desc') return b.title.localeCompare(a.title);
            if (sortBy === 'registrants_desc') return b.current_registrants - a.current_registrants;
            return 0;
        });
        return out;
    }, [filtered, sortBy]);

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(30);

    const paged = useMemo(() => {
        const start = pageIndex * pageSize;
        return sorted.slice(start, start + pageSize);
    }, [sorted, pageIndex, pageSize]);

    useEffect(() => {
        setPageIndex(0);
    }, [timePreset, roleFilter, courseFilterId, courseFilterName, sortBy, pageSize]);

    const hasFetched = !isLoading && !error;
    const showEmptyState = hasFetched && sorted.length === 0;

    return (
      <div className="flex flex-col gap-2 h-full min-h-0">
          <div className="flex gap-2 flex-wrap items-center">
              <select
                className="border rounded px-2 py-1 text-sm"
                value={timePreset}
                onChange={(e) => setTimePreset(e.target.value as TimePreset)}
              >
                  {presetOptions.map((preset) => (
                    <option key={preset} value={preset}>
                        {formatPresetLabel(preset)}
                    </option>
                  ))}
              </select>

              <select
                className="border rounded px-2 py-1 text-sm"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
              >
                  <option value="">All roles</option>
                  <option value="associated">Associated</option>
                  <option value="owner">Owner</option>
                  <option value="proctor">Proctor</option>
              </select>

              <select
                className="border rounded px-2 py-1 text-sm"
                value={courseFilterName}
                onChange={(e) => setCourseFilterName(e.target.value)}
              >
                  <option value="">All courses</option>
                  {allCourseNameOptions.map((name) => (
                    <option key={name} value={name}>
                        {name}
                    </option>
                  ))}
              </select>

              {!!courseIdOptions.length && (
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={courseFilterId}
                  onChange={(e) => setCourseFilterId(e.target.value)}
                >
                    <option value="">All course IDs</option>
                    {courseIdOptions.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                          {c.label} ({c.id})
                      </option>
                    ))}
                </select>
              )}

              <select
                className="border rounded px-2 py-1 text-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortKey)}
              >
                  <option value="event_date_desc">Sort: Start Date (newest)</option>
                  <option value="event_date_asc">Sort: Start Date (oldest)</option>
                  <option value="title_asc">Sort: Title (A→Z)</option>
                  <option value="title_desc">Sort: Title (Z→A)</option>
                  <option value="registrants_desc">Sort: Registrants (high→low)</option>
              </select>
          </div>

          {isLoading && <div className="hbr-css__text-secondary-sm">Loading events…</div>}
          {error && <div style={{ color: 'red' }}>{(error as Error).message}</div>}

          {!isLoading && !error && timePreset.startsWith('future_') && !userGeo ? (
            <div className="hbr-css__text-secondary-sm">
                Unable to determine user geo from sales level 1.
            </div>
          ) : null}

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <HbrDomTable
                autoLayout
                columns={[
                    { id: 'title', name: 'Event', width: 300, type: 'string' },
                    { id: 'event_date', name: 'Start', width: 170, type: 'date' },
                    { id: 'event_end_date', name: 'End', width: 170, type: 'date' },
                    { id: 'roles', name: 'Role', width: 160, type: 'string' },
                    { id: 'courses', name: 'Course(s)', width: 260, type: 'string' },
                    { id: 'location', name: 'Location', width: 240, type: 'string' },
                    { id: 'current_registrants', name: 'Registrants', width: 140, type: 'number' },
                ] as any}
                data={paged as any}
              >
                  {paged.flatMap((r) => {
                      const eventHref = buildEventDetailPath(
                        r.course_ids[0],
                        r.event_id,
                        eventRouteBase,
                        eventRouteBase
                      );

                      return [
                      <div
                        key={`title-${r.id}`}
                        slot={`row-${r.id}-column-title`}
                        className="min-h-8 flex items-center font-semibold"
                      >
                          {eventHref ? (
                            <button
                              className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left"
                              type="button"
                              onClick={() => navigate(eventHref)}
                              title={r.title}
                            >
                                {r.title}
                            </button>
                          ) : (
                            r.title
                          )}
                      </div>,

                      <div key={`start-${r.id}`} slot={`row-${r.id}-column-event_date`} className="min-h-8 flex items-center">
                          {r.event_date ?? '—'}
                      </div>,

                      <div key={`end-${r.id}`} slot={`row-${r.id}-column-event_end_date`} className="min-h-8 flex items-center">
                          {r.event_end_date ?? '—'}
                      </div>,

                      <div key={`roles-${r.id}`} slot={`row-${r.id}-column-roles`} className="min-h-8 flex items-center">
                          {r.roles}
                      </div>,

                      <div key={`courses-${r.id}`} slot={`row-${r.id}-column-courses`} className="min-h-8 flex items-center flex-wrap gap-2">
                          {r.course_names.length ? (
                            r.course_names.map((label, idx) => {
                                const cid = r.course_ids[idx];
                                const courseHref = buildCourseDetailPath(cid, {
                                    returnTo: eventRouteBase,
                                });
                                if (courseHref) {
                                    return (
                                      <button
                                        key={`${r.id}-course-${cid}-${idx}`}
                                        className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit"
                                        type="button"
                                        onClick={() => navigate(courseHref)}
                                        title={label}
                                      >
                                          {label}
                                      </button>
                                    );
                                }
                                return <span key={`${r.id}-course-name-${idx}`}>{label}</span>;
                            })
                          ) : r.course_ids.length ? (
                            r.course_ids.map((cid, idx) => {
                                const courseHref = buildCourseDetailPath(cid, {
                                    returnTo: eventRouteBase,
                                });
                                if (!courseHref) {
                                    return <span key={`${r.id}-course-id-${cid}-${idx}`}>Course {cid}</span>;
                                }
                                return (
                                  <button
                                    key={`${r.id}-course-id-${cid}-${idx}`}
                                    className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit"
                                    type="button"
                                    onClick={() => navigate(courseHref)}
                                  >
                                      Course {cid}
                                  </button>
                                );
                            })
                          ) : (
                            '—'
                          )}
                      </div>,

                      <div key={`location-${r.id}`} slot={`row-${r.id}-column-location`} className="min-h-8 flex items-center">
                          {r.location_label}
                      </div>,

                      <div
                        key={`registrants-${r.id}`}
                        slot={`row-${r.id}-column-current_registrants`}
                        className="min-h-8 flex items-center justify-end"
                      >
                          {r.current_registrants}
                      </div>,
                      ];
                  })}
              </HbrDomTable>

              {showEmptyState && (
                <div className="p-4 text-sm opacity-70">
                    No events match the current filters. Try changing the time range or filters.
                </div>
              )}
          </div>

          <HbrPagination
            itemCount={sorted.length}
            pageSize={pageSize}
            page={pageIndex}
            advanced
            pageInfo
            sizeChanger
            pageOptions={PAGE_SIZE_OPTIONS}
            onHbr-size-changed={(e: any) => {
                setPageSize(e.detail.pageSize);
                setPageIndex(0);
            }}
            onHbr-page-changed={(e: any) => setPageIndex(e.detail)}
          />
      </div>
    );
}